import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Code, Trophy, Calendar, Clock, ChevronRight, Star, Users, TrendingUp, 
  Play, Award, Zap, Target, Brain, Shield, Database, Cpu, Globe,
  ArrowRight, CheckCircle, Flame, Crown, BarChart3, Rocket, BookOpen, 
  Briefcase, Network, Building2, Layers, MonitorPlay, Sparkles
} from 'lucide-react';
import axios from 'axios';
import Logo from '../components/landing/Logo';
import GrandFooter from '../components/landing/GrandFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FadeInSection = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const MiniLineChart = ({ data, color = '#B91C1C', height = 40 }) => {
  const [animated, setAnimated] = useState(false);
  const chartRef = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setAnimated(true); },
      { threshold: 0.3 }
    );
    if (chartRef.current) observer.observe(chartRef.current);
    return () => observer.disconnect();
  }, []);

  const max = Math.max(...data);
  const points = data.map((val, i) => ({
    x: (i / (data.length - 1)) * 100,
    y: 100 - (val / max) * 100
  }));
  
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  return (
    <div ref={chartRef} style={{ height }} className="w-full">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
        <defs>
          <linearGradient id={`grad-${color.replace('#', '')}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.15" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={`${pathD} L 100 100 L 0 100 Z`} fill={`url(#grad-${color.replace('#', '')})`} className={animated ? 'opacity-100' : 'opacity-0'} />
        <path d={pathD} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round"
          style={{ strokeDasharray: 300, strokeDashoffset: animated ? 0 : 300, transition: 'stroke-dashoffset 1.2s cubic-bezier(0.22, 1, 0.36, 1)' }}
        />
      </svg>
    </div>
  );
};

const AnimatedCounter = ({ end, suffix = '' }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = end / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [end]);
  return <>{count.toLocaleString()}{suffix}</>;
};

const CodeArenaPage = () => {
  const navigate = useNavigate();
  const [potd, setPotd] = useState(null);
  const [stats, setStats] = useState(null);
  const [rankings, setRankings] = useState([]);
  const [contests, setContests] = useState([]);
  const [problems, setProblems] = useState([]);
  const [enterpriseChallenges, setEnterpriseChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [isNavScrolled, setIsNavScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsNavScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('codearena_token');
    setIsLoggedIn(!!token);
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, potdRes, rankingsRes, contestsRes, problemsRes, enterpriseRes] = await Promise.all([
        axios.get(`${API}/codearena/stats`).catch(() => ({ data: {} })),
        axios.get(`${API}/codearena/problems/daily`).catch(() => ({ data: {} })),
        axios.get(`${API}/codearena/rankings?limit=5`).catch(() => ({ data: { rankings: [] } })),
        axios.get(`${API}/codearena/contests?status=upcoming`).catch(() => ({ data: { contests: [] } })),
        axios.get(`${API}/codearena/problems?limit=6`).catch(() => ({ data: { problems: [] } })),
        axios.get(`${API}/public/enterprise/challenges?limit=6`).catch(() => ({ data: { challenges: [] } }))
      ]);
      
      setStats(statsRes.data);
      setPotd(potdRes.data);
      setRankings(rankingsRes.data.rankings || []);
      setContests(contestsRes.data.contests || []);
      setProblems(problemsRes.data.problems || []);
      setEnterpriseChallenges(enterpriseRes.data.challenges || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy': return 'text-[#0A0A0A] bg-[#FAFAF9] border-[#E5E5E5]';
      case 'medium': return 'text-[#B91C1C] bg-[#FEF2F2] border-[#B91C1C]/20';
      case 'hard': return 'text-[#7F1D1D] bg-[#FEF2F2] border-[#7F1D1D]/30';
      default: return 'text-[#525252] bg-[#FAFAF9] border-[#E5E5E5]';
    }
  };

  const getDomainIcon = (domain) => {
    const icons = {
      'AI / Machine Learning': Brain,
      'Data Structures & Algorithms': Database,
      'Blockchain': Shield,
      'Cloud Computing': Globe,
      'Cybersecurity': Shield,
      'Data Science': BarChart3
    };
    return icons[domain] || Code;
  };

  const howItWorksSteps = [
    {
      icon: BookOpen,
      title: 'Sign Up & Explore',
      description: 'Create your free account and browse through hundreds of coding problems.',
      stats: { label: 'Time to Start', value: '2 min' }
    },
    {
      icon: Code,
      title: 'Solve Challenges',
      description: 'Tackle the Problem of the Day and practice problems to build skills.',
      stats: { label: 'Problems', value: '500+' }
    },
    {
      icon: Trophy,
      title: 'Compete Nationally',
      description: 'Join rated contests to test your skills against developers nationwide.',
      stats: { label: 'Monthly Contests', value: '10+' }
    },
    {
      icon: Briefcase,
      title: 'Get Hired',
      description: 'Build your Skill Passport and get discovered by top companies.',
      stats: { label: 'Hiring Rate', value: '85%' }
    }
  ];

  const platformFeatures = [
    { icon: Code, title: 'Daily Challenges', description: 'New coding problems every day to keep your skills sharp.' },
    { icon: Trophy, title: 'Live Contests', description: 'Compete in rated contests and climb the leaderboard.' },
    { icon: Award, title: 'Skill Passport', description: 'Verified credentials showcasing your abilities.' },
    { icon: Briefcase, title: 'Job Opportunities', description: 'Get discovered by 100+ top hiring companies.' },
    { icon: Brain, title: 'AI-Powered Practice', description: 'Smart recommendations based on your skill level.' },
    { icon: Network, title: 'Developer Community', description: 'Connect with like-minded developers nationwide.' }
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-[#0A0A0A] selection:bg-[#FEF2F2] selection:text-[#B91C1C]" data-testid="arena-page">
      
      {/* Sticky Glass-blur Nav */}
      <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${isNavScrolled ? 'bg-white/85 backdrop-blur-md border-b border-[#E5E5E5] shadow-sm py-3' : 'bg-transparent py-5'}`} data-testid="arena-nav">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Logo subtitle="Arena" size="md" />
            
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/codearena/problems" className="text-sm font-medium text-[#525252] hover:text-[#0A0A0A] transition-colors" data-testid="arena-nav-problems">Problems</Link>
              <Link to="/codearena/contests" className="text-sm font-medium text-[#525252] hover:text-[#0A0A0A] transition-colors" data-testid="arena-nav-contests">Contests</Link>
              <Link to="/codearena/rankings" className="text-sm font-medium text-[#525252] hover:text-[#0A0A0A] transition-colors" data-testid="arena-nav-rankings">Rankings</Link>
              <Link to="/codearena/jobs" className="text-sm font-medium text-[#525252] hover:text-[#0A0A0A] transition-colors" data-testid="arena-nav-jobs">Jobs</Link>
              <Link to="/codearena/events" className="text-sm font-medium text-[#525252] hover:text-[#0A0A0A] transition-colors" data-testid="arena-nav-events">Events</Link>
            </nav>
            
            <div className="flex items-center gap-4">
              {isLoggedIn ? (
                <Link to="/codearena/dashboard" className="px-6 py-2.5 bg-[#B91C1C] text-white rounded-full text-sm font-bold shadow-[0_4px_14px_rgba(185,28,28,0.3)] hover:shadow-[0_6px_20px_rgba(185,28,28,0.4)] hover:-translate-y-0.5 transition-all">
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link to="/codearena/auth" className="hidden md:block text-sm font-semibold text-[#0A0A0A] hover:text-[#B91C1C] transition-colors" data-testid="arena-cta-signin">Sign In</Link>
                  <Link to="/codearena/auth" className="px-6 py-2.5 bg-[#B91C1C] text-white rounded-full text-sm font-bold shadow-[0_4px_14px_rgba(185,28,28,0.3)] hover:shadow-[0_6px_20px_rgba(185,28,28,0.4)] hover:-translate-y-0.5 transition-all" data-testid="arena-cta-join">Join Now</Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section — Full Viewport, Grand */}
      <section className="relative pt-32 pb-28 md:pt-40 md:pb-36 min-h-[calc(100vh-3.5rem)] flex items-center overflow-hidden" data-testid="arena-hero">
        {/* Watermark background */}
        <div className="absolute top-1/4 -right-1/4 text-[18vw] font-black text-[#B91C1C] opacity-[0.025] -rotate-12 pointer-events-none select-none tracking-tighter leading-none">
          EVALENTX
        </div>
        
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8 relative z-10 w-full">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <FadeInSection className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#FAFAF9] border border-[#E5E5E5] rounded-full text-[11px] font-bold uppercase tracking-widest text-[#B91C1C] mb-8">
                <Sparkles size={12} />
                <span>National Talent Platform</span>
              </div>
              
              <h1 className="text-6xl md:text-7xl lg:text-[7.5rem] font-extrabold text-[#0A0A0A] tracking-tighter leading-[0.95] mb-8" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                Code. Compete.<br />
                <span className="relative inline-block mt-2">
                  Get Hired.
                  <motion.div 
                    initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute -bottom-1 left-0 h-3 bg-[#B91C1C]/20 -z-10"
                  />
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-[#525252] mb-12 leading-relaxed max-w-xl font-medium">
                The premier platform to build your Skill Passport, dominate national challenges, and get discovered by the world's top tech companies.
              </p>
              
              <div className="flex flex-wrap gap-4">
                {isLoggedIn ? (
                  <Link to="/codearena/dashboard" className="px-8 py-4 bg-[#B91C1C] text-white rounded-full font-bold shadow-[0_8px_24px_rgba(185,28,28,0.25)] hover:shadow-[0_12px_32px_rgba(185,28,28,0.35)] hover:-translate-y-1 transition-all flex items-center gap-2 group">
                    My Dashboard <ArrowRight className="group-hover:translate-x-1 transition-transform" size={18} />
                  </Link>
                ) : (
                  <>
                    <Link to="/codearena/auth" className="px-8 py-4 bg-[#B91C1C] text-white rounded-full font-bold shadow-[0_8px_24px_rgba(185,28,28,0.25)] hover:shadow-[0_12px_32px_rgba(185,28,28,0.35)] hover:-translate-y-1 transition-all flex items-center gap-2 group">
                      Start Building Profile <ArrowRight className="group-hover:translate-x-1 transition-transform" size={18} />
                    </Link>
                    <Link to="/codearena/problems" className="px-8 py-4 bg-white text-[#0A0A0A] border-[1.5px] border-[#E5E5E5] hover:border-[#B91C1C] hover:bg-[#FEF2F2] rounded-full font-bold transition-all flex items-center gap-2">
                      <Code size={18} className="text-[#B91C1C]" /> Explore Challenges
                    </Link>
                  </>
                )}
              </div>
            </FadeInSection>

            {/* Premium Minimal Mockup */}
            <FadeInSection delay={0.2} className="lg:col-span-5 relative">
              <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 shadow-[0_24px_48px_-12px_rgba(0,0,0,0.05)]">
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#F5F5F5]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#0A0A0A] rounded-xl flex items-center justify-center">
                      <Trophy className="text-white" size={18} />
                    </div>
                    <div>
                      <div className="text-[#0A0A0A] font-bold text-sm">Developer Profile</div>
                      <div className="text-[#A3A3A3] text-xs font-medium uppercase tracking-wide">Verified Talent</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1 bg-[#FEF2F2] rounded-full border border-[#FEF2F2]">
                    <span className="w-1.5 h-1.5 bg-[#B91C1C] rounded-full animate-pulse" />
                    <span className="text-[#B91C1C] text-[10px] font-bold uppercase tracking-wider">Top 1%</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-[#FAFAF9] rounded-xl p-4 border border-[#F5F5F5]">
                    <div className="text-xs font-semibold text-[#A3A3A3] uppercase tracking-wider mb-1">Global Rank</div>
                    <div className="text-2xl font-black text-[#0A0A0A]">#142</div>
                    <MiniLineChart data={[200, 180, 160, 150, 142]} color="#0A0A0A" height={30} />
                  </div>
                  <div className="bg-[#FEF2F2] rounded-xl p-4 border border-[#B91C1C]/10">
                    <div className="text-xs font-semibold text-[#B91C1C] uppercase tracking-wider mb-1">Skill Score</div>
                    <div className="text-2xl font-black text-[#B91C1C]">2,840</div>
                    <MiniLineChart data={[1500, 1800, 2100, 2400, 2840]} color="#B91C1C" height={30} />
                  </div>
                </div>

                <div className="bg-[#0A0A0A] rounded-xl p-4 font-mono text-sm shadow-inner relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-2 opacity-20"><Code size={64} color="#FFF" /></div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#B91C1C]"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-[#525252]"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-[#525252]"></div>
                    <span className="text-[#A3A3A3] text-[10px] ml-2 tracking-widest uppercase">solution.cpp</span>
                  </div>
                  <div className="text-[#F5F5F5] opacity-90 leading-relaxed text-xs space-y-1">
                    <div><span className="text-[#B91C1C]">class</span> Solution {'{'}</div>
                    <div className="pl-4"><span className="text-[#B91C1C]">public:</span></div>
                    <div className="pl-4">int maxProfit(vector&lt;int&gt;& prices) {'{'}</div>
                    <div className="pl-8">int profit = 0;</div>
                    <div className="pl-8">for (int i = 1; i &lt; prices.size(); i++)</div>
                    <div className="pl-12 text-white font-semibold">profit += max(0, prices[i] - prices[i-1]);</div>
                    <div className="pl-8 text-white font-semibold">return profit;</div>
                    <div className="pl-4">{'}'}</div>
                    <div>{'}'}</div>
                  </div>
                </div>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* Premium Category Cards Grid */}
      <section className="py-16 bg-[#FAFAF9] border-y border-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            
            {/* Jobs Card */}
            <Link to="/codearena/jobs" className="group bg-white rounded-2xl border border-[#E5E5E5] p-8 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_24px_48px_-12px_rgba(185,28,28,0.12)] hover:border-[#B91C1C]/30 transition-all duration-300" data-testid="category-card-jobs">
              <div className="w-14 h-14 bg-[#FEF2F2] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Briefcase className="text-[#B91C1C]" size={28} />
              </div>
              <h3 className="text-2xl font-black text-[#0A0A0A] tracking-tight mb-3">Jobs & Internships</h3>
              <p className="text-[#525252] text-sm leading-relaxed mb-8">Access pre-vetted opportunities from elite tech companies actively hiring.</p>
              <div className="flex items-center justify-between mt-auto">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#FAFAF9] border border-[#E5E5E5] rounded-full text-xs font-bold text-[#0A0A0A]">
                  100+ Hiring Partners
                </span>
                <div className="text-[#B91C1C] flex items-center font-bold text-sm">
                  Explore <ArrowRight size={16} className="ml-1 group-hover:translate-x-1.5 transition-transform" />
                </div>
              </div>
            </Link>

            {/* Challenges Card */}
            <Link to="/codearena/contests" className="group bg-white rounded-2xl border border-[#E5E5E5] p-8 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_24px_48px_-12px_rgba(185,28,28,0.12)] hover:border-[#B91C1C]/30 transition-all duration-300" data-testid="category-card-contests">
              <div className="w-14 h-14 bg-[#FEF2F2] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Trophy className="text-[#B91C1C]" size={28} />
              </div>
              <h3 className="text-2xl font-black text-[#0A0A0A] tracking-tight mb-3">Challenges & Contests</h3>
              <p className="text-[#525252] text-sm leading-relaxed mb-8">Compete in national hackathons and climb the live leaderboards.</p>
              <div className="flex items-center justify-between mt-auto">
                {contests.length > 0 ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#FEF2F2] border border-[#B91C1C]/20 rounded-full text-xs font-bold text-[#B91C1C]">
                    <span className="w-1.5 h-1.5 bg-[#B91C1C] rounded-full animate-pulse" />
                    {contests.length} Live Contests
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#FAFAF9] border border-[#E5E5E5] rounded-full text-xs font-bold text-[#525252]">
                    Upcoming weekly
                  </span>
                )}
                <div className="text-[#B91C1C] flex items-center font-bold text-sm">
                  Explore <ArrowRight size={16} className="ml-1 group-hover:translate-x-1.5 transition-transform" />
                </div>
              </div>
            </Link>

            {/* Mock Interviews Card */}
            <Link to="/codearena/mock-interview" className="group bg-white rounded-2xl border border-[#E5E5E5] p-8 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_24px_48px_-12px_rgba(185,28,28,0.12)] hover:border-[#B91C1C]/30 transition-all duration-300" data-testid="category-card-mock-interview">
              <div className="w-14 h-14 bg-[#FEF2F2] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <MonitorPlay className="text-[#B91C1C]" size={28} />
              </div>
              <h3 className="text-2xl font-black text-[#0A0A0A] tracking-tight mb-3">AI Mock Interviews</h3>
              <p className="text-[#525252] text-sm leading-relaxed mb-8">Practice with voice-enabled AI technical interviews and get instant feedback.</p>
              <div className="flex items-center justify-between mt-auto">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#FAFAF9] border border-[#E5E5E5] rounded-full text-xs font-bold text-[#0A0A0A]">
                  New Feature
                </span>
                <div className="text-[#B91C1C] flex items-center font-bold text-sm">
                  Explore <ArrowRight size={16} className="ml-1 group-hover:translate-x-1.5 transition-transform" />
                </div>
              </div>
            </Link>

          </div>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="py-16 bg-white" data-testid="arena-stats">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 divide-x-0 md:divide-x divide-[#F5F5F5]">
            {[
              { label: 'Verified Developers', value: stats?.total_students || 50000, suffix: '+' },
              { label: 'Coding Challenges', value: stats?.total_problems || 500, suffix: '+' },
              { label: 'Hiring Partners', value: 100, suffix: '+', isHighlight: true },
              { label: 'Placements', value: 2500, suffix: '+' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center px-4">
                <div className={`text-4xl md:text-5xl font-black mb-2 tracking-tighter ${stat.isHighlight ? 'text-[#B91C1C]' : 'text-[#0A0A0A]'}`}>
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-xs font-bold text-[#A3A3A3] uppercase tracking-widest">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem of the Day & Rankings */}
      <section className="py-24 bg-[#FAFAF9] border-t border-[#F5F5F5]" data-testid="arena-potd">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            
            {/* POTD Premium Editorial Card */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-2 h-8 bg-[#B91C1C] rounded-full"></div>
                <h2 className="text-3xl font-black text-[#0A0A0A] tracking-tight">Problem of the Day</h2>
              </div>
              
              <div className="bg-white border border-[#E5E5E5] rounded-3xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.04)] relative">
                <div className="absolute top-0 right-0 p-8 opacity-[0.03] pointer-events-none"><Star size={120} /></div>
                
                <div className="p-8 lg:p-10 relative z-10">
                  <div className="flex items-center justify-between mb-8 border-b border-[#F5F5F5] pb-6">
                    <div className="text-sm font-bold text-[#A3A3A3] uppercase tracking-widest flex items-center gap-2">
                      <Calendar size={16} className="text-[#0A0A0A]" />
                      {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </div>
                    <div className="flex items-center gap-2 bg-[#FEF2F2] px-4 py-1.5 rounded-full border border-[#B91C1C]/20">
                      <Flame className="text-[#B91C1C]" size={16} />
                      <span className="text-[#B91C1C] font-black text-sm">+{potd?.problem?.points || 50} pts</span>
                    </div>
                  </div>
                  
                  {loading ? (
                    <div className="animate-pulse space-y-4">
                      <div className="h-10 bg-[#F5F5F5] rounded-lg w-3/4"></div>
                      <div className="h-4 bg-[#F5F5F5] rounded w-1/2"></div>
                    </div>
                  ) : potd?.problem ? (
                    <div>
                      <h3 className="text-3xl font-black text-[#0A0A0A] mb-4 tracking-tight leading-tight">{potd.problem.title}</h3>
                      <div className="flex flex-wrap items-center gap-3 mb-8">
                        <span className={`px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-wider border ${getDifficultyColor(potd.problem.difficulty)}`}>
                          {potd.problem.difficulty || 'MEDIUM'}
                        </span>
                        {potd.problem.domains?.slice(0, 2).map((domain, idx) => (
                          <span key={idx} className="flex items-center gap-1.5 text-xs font-bold text-[#525252] bg-[#FAFAF9] px-3 py-1.5 rounded-full border border-[#E5E5E5]">
                            {React.createElement(getDomainIcon(domain), { size: 14 })}
                            {domain}
                          </span>
                        ))}
                      </div>
                      <p className="text-[#525252] mb-10 text-lg leading-relaxed line-clamp-3">
                        {potd.problem.description?.substring(0, 200)}...
                      </p>
                      <Link
                        to={`/codearena/problem/${potd.problem.id}`}
                        className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#171717] text-white px-8 py-4 rounded-full font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
                      >
                        <Play size={18} fill="currentColor" />
                        Solve Challenge Now
                      </Link>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Code className="mx-auto text-[#E5E5E5] mb-4" size={48} />
                      <p className="text-[#A3A3A3] font-medium">No problem available today. Check back soon.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Top Rankings */}
            <div className="lg:col-span-1" data-testid="arena-rankings-list">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-black text-[#0A0A0A] tracking-tight">Leaderboard</h2>
                </div>
                <Link to="/codearena/rankings" className="text-[#B91C1C] hover:text-[#7F1D1D] text-sm font-bold tracking-wide">View All</Link>
              </div>

              <div className="bg-white border border-[#E5E5E5] rounded-3xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
                <div className="divide-y divide-[#F5F5F5]">
                  {rankings.length > 0 ? rankings.map((user, idx) => (
                    <div key={idx} className="p-5 flex items-center gap-4 hover:bg-[#FAFAF9] transition-colors group">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-sm border-2 ${
                        idx === 0 ? 'bg-[#FEF2F2] border-[#B91C1C] text-[#B91C1C]' :
                        'bg-[#FAFAF9] border-[#E5E5E5] text-[#A3A3A3] group-hover:border-[#0A0A0A] group-hover:text-[#0A0A0A]'
                      }`}>
                        {idx === 0 ? <Crown size={16} strokeWidth={3} /> : idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-[#171717] truncate">{user.full_name || 'Developer'}</div>
                        <div className="text-xs text-[#A3A3A3] font-medium">{user.college || 'Student'}</div>
                      </div>
                      <div className="text-right">
                        <div className={`font-black ${idx === 0 ? 'text-[#B91C1C]' : 'text-[#0A0A0A]'}`}>{user.total_score || 0}</div>
                        <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">PTS</div>
                      </div>
                    </div>
                  )) : (
                    <div className="p-8 text-center text-[#A3A3A3] font-medium">No rankings yet.</div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Enterprise Challenges Strip */}
      {enterpriseChallenges.length > 0 && (
        <section className="py-24 bg-white border-t border-[#F5F5F5]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center justify-between mb-12">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-[#B91C1C] mb-2">Hiring Events</div>
                <h2 className="text-3xl md:text-4xl font-black text-[#0A0A0A] tracking-tight">Enterprise Challenges</h2>
              </div>
              <Link to="/codearena/contests" className="hidden md:flex items-center gap-2 text-[#0A0A0A] hover:text-[#B91C1C] font-bold transition-colors">
                View All <ArrowRight size={18} />
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {enterpriseChallenges.map((challenge) => (
                <Link
                  key={challenge.id}
                  to={`/enterprise/${challenge.org_slug || 'org'}/${challenge.id}`}
                  className="group bg-[#FAFAF9] border border-[#E5E5E5] rounded-3xl overflow-hidden hover:border-[#0A0A0A] hover:shadow-[0_12px_32px_rgba(0,0,0,0.08)] transition-all duration-300 flex flex-col"
                >
                  <div className="h-40 bg-[#0A0A0A] relative overflow-hidden">
                    {(challenge.banner_image || challenge.landing_page?.hero_image) && (
                      <img src={challenge.banner_image || challenge.landing_page?.hero_image} alt={challenge.title} className="w-full h-full object-cover opacity-80 group-hover:scale-105 group-hover:opacity-100 transition-all duration-500" />
                    )}
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur px-3 py-1 text-[#0A0A0A] text-[10px] font-black uppercase tracking-widest rounded-full">
                      Hiring
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-2 text-xs font-bold text-[#A3A3A3] uppercase tracking-wide mb-3">
                      <Building2 size={14} /> {challenge.company_name || 'Partner Company'}
                    </div>
                    <h3 className="font-black text-xl text-[#0A0A0A] group-hover:text-[#B91C1C] transition-colors mb-3 line-clamp-2">
                      {challenge.title}
                    </h3>
                    <div className="flex-1"></div>
                    <div className="flex items-center justify-between pt-6 mt-6 border-t border-[#E5E5E5]">
                      <div className="flex items-center gap-2 text-sm font-bold text-[#525252]">
                        <Users size={16} className="text-[#A3A3A3]" /> {challenge.registration_count || 0} Joined
                      </div>
                      <span className="text-sm text-[#0A0A0A] font-bold group-hover:translate-x-1 transition-transform">
                        <ArrowRight size={18} />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Practice Problems Grid */}
      <section className="py-24 bg-[#FAFAF9] border-t border-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-black text-[#0A0A0A] tracking-tight">Practice Problems</h2>
              <p className="text-[#525252] mt-2 font-medium">Sharpen your logic across domains.</p>
            </div>
            <Link to="/codearena/problems" className="hidden md:flex items-center gap-2 px-6 py-2.5 bg-white border-2 border-[#E5E5E5] hover:border-[#0A0A0A] rounded-full text-[#0A0A0A] font-bold transition-all">
              View All
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {problems.length > 0 ? problems.map((problem, idx) => (
              <Link
                key={idx}
                to={`/codearena/problem/${problem.id}`}
                className="bg-white border border-[#E5E5E5] rounded-2xl p-6 hover:border-[#0A0A0A] hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] transition-all group flex flex-col"
              >
                <div className="flex items-start justify-between mb-4">
                  <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${getDifficultyColor(problem.difficulty)}`}>
                    {problem.difficulty || 'EASY'}
                  </span>
                  <span className="text-[#171717] font-black text-sm">+{problem.points} pts</span>
                </div>
                <h3 className="text-lg font-black text-[#0A0A0A] mb-3 group-hover:text-[#B91C1C] transition-colors leading-tight">
                  {problem.title}
                </h3>
                <div className="mt-auto pt-4 flex flex-wrap gap-2">
                  {problem.tags?.slice(0, 3).map((tag, tidx) => (
                    <span key={tidx} className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] bg-[#FAFAF9] px-2 py-1 rounded border border-[#F5F5F5]">
                      {tag}
                    </span>
                  ))}
                </div>
              </Link>
            )) : (
              <div className="col-span-full bg-white border border-[#E5E5E5] rounded-2xl p-12 text-center">
                <Code className="mx-auto text-[#E5E5E5] mb-4" size={48} />
                <h3 className="font-bold text-[#171717] mb-2">No Problems Yet</h3>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Skill Domains — Grand 8-domain grid */}
      <section className="py-24 md:py-36 bg-white border-t border-[#E5E5E5]" data-testid="arena-skill-domains">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <p className="text-[11px] font-bold text-[#A3A3A3] uppercase tracking-[0.25em] mb-4">Skill Domains</p>
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter text-[#0A0A0A] leading-[0.95]" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                Master <span className="text-[#B91C1C]">eight</span> domains.
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-lg md:text-xl text-[#525252] font-medium leading-relaxed">
                From classic algorithms to AI/ML, blockchain, and system design — sharpen the exact skills companies are hiring for, with curated tracks and real-world problems.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {[
              { icon: Layers, name: 'DSA', count: '180+', testid: 'domain-dsa' },
              { icon: Brain, name: 'AI / ML', count: '95+', testid: 'domain-ai' },
              { icon: Globe, name: 'Web Dev', count: '120+', testid: 'domain-web' },
              { icon: Network, name: 'Cloud', count: '60+', testid: 'domain-cloud' },
              { icon: Shield, name: 'Cybersecurity', count: '50+', testid: 'domain-security' },
              { icon: Database, name: 'Blockchain', count: '40+', testid: 'domain-blockchain' },
              { icon: Cpu, name: 'DevOps', count: '45+', testid: 'domain-devops' },
              { icon: BarChart3, name: 'System Design', count: '35+', testid: 'domain-system-design' },
            ].map((d) => (
              <Link
                key={d.name}
                to="/codearena/problems"
                className="group bg-white border border-[#E5E5E5] rounded-2xl p-6 md:p-8 hover:border-[#B91C1C] hover:shadow-[0_24px_48px_-12px_rgba(185,28,28,0.18)] hover:-translate-y-1 transition-all duration-500"
                data-testid={d.testid}
              >
                <div className="w-12 h-12 rounded-xl bg-[#FAFAF9] border border-[#E5E5E5] grid place-items-center mb-5 group-hover:bg-[#FEF2F2] group-hover:border-[#B91C1C]/30 transition-colors">
                  <d.icon size={20} className="text-[#0A0A0A] group-hover:text-[#B91C1C] transition-colors" />
                </div>
                <h3 className="text-xl md:text-2xl font-extrabold tracking-tight text-[#0A0A0A] mb-1 leading-tight" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                  {d.name}
                </h3>
                <p className="text-sm font-semibold text-[#A3A3A3] mb-5">{d.count} problems</p>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0A0A0A] group-hover:text-[#B91C1C] uppercase tracking-[0.15em] transition-colors">
                  Explore <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Banner */}
      <section className="py-24 md:py-32 bg-[#0A0A0A] relative overflow-hidden" data-testid="arena-cta-bottom">
        {/* Abstract shapes */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#B91C1C] rounded-full mix-blend-multiply filter blur-[128px] opacity-40"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#7F1D1D] rounded-full mix-blend-multiply filter blur-[128px] opacity-40"></div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 tracking-tighter" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
            Ready to Get Hired?
          </h2>
          <p className="text-xl text-[#A3A3A3] mb-12 font-medium max-w-2xl mx-auto">
            Build your profile, prove your skills, and let top tech companies compete for you.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/codearena/auth"
              className="px-10 py-4 bg-[#B91C1C] text-white rounded-full font-bold shadow-[0_8px_24px_rgba(185,28,28,0.4)] hover:shadow-[0_12px_32px_rgba(185,28,28,0.6)] hover:-translate-y-1 transition-all flex items-center justify-center gap-2 group"
            >
              Join Now — It's Free <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Grand Footer */}
      <GrandFooter />

    </div>
  );
};

export default CodeArenaPage;